import {
  ColumnFilter,
  DataTable,
  DataTableOptions,
  DataType,
  DecimalScale,
  FilterType,
  FormDialogMode,
  SelectOption,
  SortDirection,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Build, Delete, Edit, Pageview, Send } from '@material-ui/icons'
import BlockIcon from '@material-ui/icons/Block'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { EnumEstadoViajeOpLogistica } from '../../../../api/oData/enums/enumEstadoViajeOpLogistica'
import { NotificationMessageIcons } from '../../../../components'
import { DataTableErrorHandler } from '../../../../components/errors/scopes/dataTable/dataTableError'
import Page from '../../../../components/page/page'
import PageHeader from '../../../../components/page/pageHeader'
import CustomFilter from '../../../../components/shared/customFilter/customFilter'
import DateFilter from '../../../../components/shared/dateFilter/dateFilter'
import { parseDateFilter } from '../../../../components/shared/dateFilter/dateFilter.helpers'
import GenericFilters from '../../../../components/shared/genericFilter/genericFilters'
import { generateIconsFromConfiguration } from '../../../../helpers/dataTableActionIconHelper'
import { normalizeFilename } from '../../../../helpers/fileDownloadHelper'
import { getUserPermissions, hasPermission, hasRole } from '../../../../helpers/permissionsHelper'
import {
  FormDialogDefinitionProps,
  useConfirmationDialog,
  useFormDialog,
  useHttpError,
  usePageState
} from '../../../../hooks'
import useRetries from '../../../../hooks/useRetries'
import {
  ActionIcons,
  DateFilters,
  DatePeriod,
  GlobalNotification,
  NonAuthRoutes,
  UserRoles
} from '../../../../model'
import { ConfirmationDialogs } from '../../../../model/enums/confirmationDialogs'
import { Pages, Resources } from '../../../../model/RBAC/constants'
import { ServiceResponse } from '../../../../model/serviceResponse'
import {
  getCustomerDomain,
  notifyError,
  RootStoreModel,
  storeCustomFilterForCurrentPage,
  storeDataTableFilterForCurrentPage,
  storeDataTableSearchForCurrentPage,
  storeDateFilterForCurrentPage
} from '../../../../redux'
import { canCancelMassive, filterApplicableActionsConfiguration } from '../../tanks.helpers'
import { mapOriginPlantsFromFetchedTanksArray, mapTanksDataToDataTable } from '../../tanks.mapper'
import { TankModel, TankModelPatch } from '../../tanks.model'
import {
  cancelTanks,
  checkTanksState,
  deleteTank,
  editTank,
  fetchCarriers,
  fetchClients,
  fetchDestinations,
  fetchOpsInActiveDispatch,
  fetchTankOpsToDispatch,
  fetchTanksListItemsByRegisteredState,
  fetchTanksListItemsByValidatedState,
  fetchTankTravel,
  sendTanksMassive
} from '../../tanks.service'
import { GenericFilter, getFilters, getTanksListColumns } from './tanksList.config'

const DEFAULT_ACTIVE_FILTER = DateFilters.TOMORROW_ONLY

export interface TanksListProps {
  fetchService: ({
    startDate,
    endDate,
    clientID,
    lastHundredTanks
  }) => Promise<ServiceResponse<TankModel[] | undefined>>
}

export const TanksList: React.FC<TanksListProps> = ({ fetchService }) => {
  const [t] = useTranslation(['globalerisLibrary', 'tanks', 'enums'])
  let history = useHistory()
  const { info } = useNotify()

  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const { openForm, updateFormDefinition, closeForm, formProps, getFormData, CustomFormDialog } =
    useFormDialog()
  const [globalNotification, setGlobalNotification] = React.useState<
    GlobalNotification | undefined
  >(undefined)

  const [isFetching, setIsFetching] = useState<boolean>(false)
  const { dateFilter, customFilter } = usePageState()
  const filters = useMemo(() => getFilters(dateFilter, customFilter), [])
  const initialGenericFilter: GenericFilter | undefined =
    !!dateFilter || !!customFilter ? undefined : filters.genericFilters[0]

  const [genericFilter, setGenericFilter] = useState<GenericFilter | undefined>(
    initialGenericFilter
  )

  const { showError, clearError, errorProps } = useHttpError()
  const [data, setData] = useState<TankModel[] | undefined>(undefined)
  // Dependencies used for Edit Quantity options
  const [editTankFormDeps, setEditTankFormDeps] = useState<any>({ formState: {} })

  const [dateRange, setDateRange] = React.useState<DatePeriod>({
    start: dateFilter
      ? moment(dateFilter.initDate).toDate()
      : customFilter
        ? moment(customFilter.startDate).toDate()
        : parseDateFilter(DEFAULT_ACTIVE_FILTER, 'start'),
    end: dateFilter
      ? moment(dateFilter.endDate).toDate()
      : customFilter
        ? moment(customFilter.endDate).toDate()
        : parseDateFilter(DEFAULT_ACTIVE_FILTER, 'end')
  })

  const [clients, setClients] = useState<SelectOption[]>([])
  const [carriers, setCarriers] = useState<SelectOption[]>([])
  const [destinations, setDestinations] = useState<SelectOption[]>([])
  const [originPlants, setOriginPlants] = useState<SelectOption[]>([])

  const activeCompany = useSelector(
    (state: RootStoreModel) => state.profile?.activeCompany && state.profile?.activeCompany
  )

  const activeBalanceZone = useSelector(
    (state: RootStoreModel) => state.profile?.activeBalanceZone && state.profile?.activeBalanceZone
  )

  const { error } = useNotify()

  // function isForUserFetch(
  //   service: (args: any) => any
  // ): service is (args: FetchTanksParams & { lastHundredTanks?: boolean }) => any {
  //   return typeof service === 'function' && service.name === 'fetchTanksListItemsForUser'
  // }

  //#region useEffect

  useEffect(() => {
    getClients()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBalanceZone, activeCompany, dateRange, dateFilter, customFilter, genericFilter])

  useEffect(() => {
    const getCarriers = async () => {
      setIsFetching(true)
      setCarriers(
        await fetchCarriers().then(response => {
          setIsFetching(false)
          if (response.success) {
            return response.data
          } else {
            showError({
              title: t('tanks:dataTable.error.fetchCarrierError'),
              errorCode: response.error?.code,
              onRefresh: getCarriers
            })
            return []
          }
        })
      )
    }

    const getDestinations = async () => {
      await fetchDestinations(undefined, undefined, undefined).then(response => {
        if (response.success && response.data) {
          setDestinations(response.data)
        }
      })
    }

    getDestinations()
    getCarriers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getClients = async () => {
    const clientsList = await fetchClients()
    setClients(clientsList)
    getTanks(clientsList.length === 1 ? +clientsList[0].value : undefined, clientsList)
  }

  const getTanks = async (clientID?: number, clients?: SelectOption[]) => {
    const client = clientID || (clients && clients.length === 1 ? +clients[0].value : undefined)

    const tanks = await getTanksList(client)
    setData(mapTanksDataToDataTable(tanks))
    setOriginPlants(mapOriginPlantsFromFetchedTanksArray(tanks))
  }

  //#

  //#region retries

  const [startRetriesTimer, stopRetriesTimer, isRetriesTimerActive] = useRetries(
    async (pendingTanks: TankModel[]) => {
      pendingTanks = await monitorTanks(pendingTanks)

      if (pendingTanks.length === 0) {
        isRetriesTimerActive && stopRetriesTimer()
        setGlobalNotification(undefined)
        info(t('tanks:notifications.success.retriesTSOFinish'))
        return
      }
    },
    5000,
    {
      immediate: true,
      onRetry: () => {
        setGlobalNotification({
          icon: NotificationMessageIcons.LOADING,
          message: t('tanks:globalNotifications.retries.tso.messages.onRetry'),
          actions: [
            {
              label: t('tanks:globalNotifications.retries.tso.actions.cancel'),
              onClick: () => {
                isRetriesTimerActive && stopRetriesTimer()
                setGlobalNotification({
                  message: t('tanks:globalNotifications.retries.tso.messages.onStop'),
                  actions: [
                    {
                      label: t('tanks:globalNotifications.retries.tso.actions.refresh'),
                      onClick: async () => {
                        getTanks()
                      }
                    },
                    {
                      label: t('tanks:globalNotifications.retries.tso.actions.close'),
                      onClick: () => {
                        setGlobalNotification(undefined)
                      }
                    }
                  ]
                })
              }
            }
          ]
        })
      },
      onFinishRetries: () => {
        setGlobalNotification({
          icon: NotificationMessageIcons.INFO,
          message: t('tanks:globalNotifications.retries.tso.messages.onFinish'),
          actions: [
            {
              label: t('tanks:globalNotifications.retries.tso.actions.refresh'),
              onClick: async () => {
                getTanks()
              }
            },
            {
              label: t('tanks:globalNotifications.retries.tso.actions.close'),
              onClick: () => {
                setGlobalNotification(undefined)
              }
            }
          ]
        })
      },
      onError: () => {
        setGlobalNotification({
          icon: NotificationMessageIcons.ERROR,
          message: t('tanks:globalNotifications.retries.tso.messages.onError'),
          actions: [
            {
              label: t('tanks:globalNotifications.retries.tso.actions.refresh'),
              onClick: async () => {
                getTanks()
              }
            }
          ]
        })
      }
    }
  )

  //#endregion

  //#region Handlers

  const onSelectGenericFilter = (value: any, filter: GenericFilter | undefined) => {
    setGenericFilter(filter)
    storeCustomFilterForCurrentPage(undefined)
  }

  const onSelectDateHandler = (
    startDate: Date,
    endDate: Date,
    activeDateFilter?: DateFilters
  ): void => {
    setGenericFilter(undefined)
    setDateRange({ start: startDate, end: endDate })
    storeDateFilterForCurrentPage({
      activeDateFilter: activeDateFilter,
      initDate: startDate,
      endDate: endDate
    })
  }

  const onApplyCustomFilter = async (filterToApply: any) => {
    setGenericFilter(undefined)
    setDateRange({
      start: filterToApply.startDate,
      end: filterToApply.endDate
    })
    storeCustomFilterForCurrentPage(filterToApply)
  }

  const onCancelActionHandler = async (rows: TankModel[]): Promise<void> => {
    var idsInDispatchProcess = await fetchOpsInActiveDispatch(rows)
    if (
      idsInDispatchProcess.data?.length === undefined ||
      idsInDispatchProcess.data?.length === 0
    ) {
      await getConfirmation(ConfirmationDialogs.CANCEL).then(async () => {
        if (canCancelMassive(rows)) {
          await cancelTanks(rows.map(row => row.id)).then(async res => {
            if (res.success) {
              getTanks()
            }
          })
        } else {
          error(`${t('tanks:dataTable.error.canCancel')}`)
        }
      })
      closeConfirmation()
    } else {
      if (idsInDispatchProcess.data?.length === 1) {
        info(t('tanks:notifications.confirmationDialog.tankInActiveDispatch'))
      } else {
        info(t('tanks:notifications.confirmationDialog.tanksInActiveDispatch'))
      }
    }
  }

  const onSendActionHandler = async (rows: TankModel[]): Promise<void> => {
    try {
      const response = await fetchTankOpsToDispatch(rows)

      if (!response.success) {
        notifyError(`${t('tanks:dataTable.error.massiveDispatchesError')}`)
        return
      }

      const missingOrderNumber = response.data!.some(tank => {
        const tripStateEnumValue =
          EnumEstadoViajeOpLogistica[tank.tripState as keyof typeof EnumEstadoViajeOpLogistica]

        return (
          [
            EnumEstadoViajeOpLogistica.Programado,
            EnumEstadoViajeOpLogistica.Viable,
            EnumEstadoViajeOpLogistica.NoViable
          ].includes(tripStateEnumValue) && tank.OrderNumber == null
        )
      })

      await getConfirmation(
        ConfirmationDialogs.SEND_TANK_MASSIVE,
        response.data!.length > 1
          ? t('confirmDialogs:SEND_TANKS_MASSIVE.message', { count: response.data!.length })
          : t('confirmDialogs:SEND_TANK_MASSIVE.message', { count: response.data!.length }),
        true
      )

      if (missingOrderNumber) {
        await getConfirmation(
          ConfirmationDialogs.SEND_TANK_NO_ORDER_NUMBER,
          t('confirmDialogs:SEND_TANK_NO_ORDER_NUMBER.message'),
          true
        )
      }

      const sendResponse = await sendTanksMassive(response.data!)
      if (sendResponse.success) {
        info(t('tanks:notifications.success.send'))
        getTanks()
      } else {
        notifyError(`${t('tanks:dataTable.error.massiveDispatchesError')}`)
      }
      closeConfirmation()
    } catch {
      notifyError(`${t('tanks:dataTable.error.massiveDispatchesError')}`)
    }
  }

  const onRowEdit = async (row: TankModel, readOnly: boolean) => {
    var idsInDispatchProcess = await fetchOpsInActiveDispatch([row])
    if (
      idsInDispatchProcess.data?.length === undefined ||
      idsInDispatchProcess.data?.length === 0
    ) {
      const tank = data?.find(t => t.id === row.id)
      var idsInDispatchProcess = await fetchOpsInActiveDispatch([tank])
      if (
        idsInDispatchProcess.data?.length === undefined ||
        idsInDispatchProcess.data?.length === 0
      ) {
        if (tank) {
          history.push(NonAuthRoutes.tanksEdit, {
            tank,
            readOnly
          })
        }

        return true
      } else {
        info(t('tanks:notifications.success.retriesTSOFinish'))
        return false
      }
    } else {
      if (idsInDispatchProcess.data?.length === 1) {
        info(t('tanks:notifications.confirmationDialog.tankInActiveDispatch'))
      } else {
        info(t('tanks:notifications.confirmationDialog.tanksInActiveDispatch'))
      }
    }
  }

  const onRowEditTravel = async (item, patchData: TankModelPatch) => {
    if (item) {
      const response = await fetchTankTravel(item.id)
      if (!response.success) {
        return
      }
      const tankDetails = response?.data && response.data.length > 0 ? response.data[0] : null

      if (tankDetails) {
        await editTank(tankDetails, patchData).then(response => {
          if (response.success) {
            info(t('tanks:notifications.success.updateTank'))
            getTanks()
          } else {
            error(t('tanks:notifications.error.updateTank'))
          }
        })
      }
    }

    closeForm()
    return true
  }

  const onEditTravelHandler = async (tankItem: TankModel) => {
    var idsInDispatchProcess = await fetchOpsInActiveDispatch([tankItem])
    if (
      idsInDispatchProcess.data?.length === undefined ||
      idsInDispatchProcess.data?.length === 0
    ) {
      openForm({
        definition: tankEditQuantityFormDef,
        title: t('tanks:forms.editTravel.title'),
        mode: FormDialogMode.EDIT,
        isWaiting: false,
        inputData: tankItem,
        onClose: formData => {
          const patchData: TankModelPatch = {
            quantity: formData?.quantity,
            kg: formData?.kg,
            pcs: formData?.pcs,
            deliveryNote: formData?.deliveryNote,
            dischargeDate: formData?.dischargeDate
          }
          formData ? onRowEditTravel(tankItem, patchData) : closeForm()
        }
      })
    } else {
      if (idsInDispatchProcess.data?.length === 1) {
        info(t('tanks:notifications.confirmationDialog.tankInActiveDispatch'))
      } else {
        info(t('tanks:notifications.confirmationDialog.tanksInActiveDispatch'))
      }
    }
    // setEditTankFormDeps({ formState: tankItem })
  }

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity) {
      const formData: TankModel = getFormData(tankEditQuantityFormDef.id)
      const updatedData = {
        ...formData,
        quantity: newQuantity,
        kg: formData.pcs ? newQuantity / formData.pcs : formData.kg
      }

      updateFormDefinition(tankEditQuantityFormDef, updatedData)
    }
  }

  const handlePcsChange = (newValue: number) => {
    if (newValue) {
      const formData: TankModel = getFormData(tankEditQuantityFormDef.id)
      const updatedData = {
        ...formData,
        kg: formData?.quantity ? formData.quantity / +newValue : formData.kg
      }

      updateFormDefinition(tankEditQuantityFormDef, updatedData)
    }
  }

  const tankEditQuantityFormDef: FormDialogDefinitionProps =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT_QUANTITY',
        inputs: [
          {
            name: 'quantity',
            label: t('tanks:forms.add.inputs.carrierQuantity'),
            type: DataType.NUMBER,
            inputSize: 6,
            decimalScale: DecimalScale.DEFAULT,
            onChange: (newQuantity, formState: any) => {
              handleQuantityChange(+newQuantity)
            }
          },
          {
            name: 'pcs',
            label: t('tanks:forms.add.inputs.pcs'),
            type: DataType.NUMBER,
            decimalScale: DecimalScale.DEFAULT,
            required: true,
            // readOnly: readOnly,
            onChange: (newValue, formState: any) => {
              if (formState.quantity !== undefined && +newValue) {
                handlePcsChange(+newValue)
              }
            }
          },
          {
            name: 'kg',
            label: t('tanks:forms.add.inputs.kg'),
            type: DataType.NUMBER,
            required: true
            // readOnly: readOnly
            // TODO Globaleris UI: loading state in Text.tsx & Number.tsx.
            // isLoading: isFetchingDeps.kg,
            // loadingText: 'Cargando tamaños de Cisternas'
          },
          {
            name: 'deliveryNote',
            label: t('tanks:forms.add.inputs.deliveryNote'),
            type: DataType.TEXT
            // readOnly: readOnly
          },
          {
            name: 'dischargeDate',
            label: t('tanks:forms.add.inputs.dischargeDate'),
            type: DataType.DATE
          }
        ]
      }
    }, [editTankFormDeps])
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const onRowDelete = async (row: TankModel) => {
    var idsInDispatchProcess = await fetchOpsInActiveDispatch([row])
    if (
      idsInDispatchProcess.data?.length === undefined ||
      idsInDispatchProcess.data?.length === 0
    ) {
      await getConfirmation(ConfirmationDialogs.DELETE).then(async () => {
        await deleteTank(row.id!).then(async response => {
          if (response.success) {
            info(t('tanks:notifications.success.delete'))
            getTanks()
          } else {
            error(t('tanks:notifications.error.delete'))
          }
        })
      })

      closeConfirmation()
      return true
    } else {
      if (idsInDispatchProcess.data?.length === 1) {
        info(t('tanks:notifications.confirmationDialog.tankInActiveDispatch'))
      } else {
        info(t('tanks:notifications.confirmationDialog.tanksInActiveDispatch'))
      }
    }
  }

  //#endregion
  const dataTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 3,
    filterType: FilterType.ADVANCED,
    download: true,
    filename: normalizeFilename(t('tanks:title')),
    print: false,
    selectableRows: true,
    customInsertEditModal: true,
    rowsPerPage: 20,
    rowsPerPageOptions: [20, 50, 100],
    canEdit: false,
    canDelete: false,
    analytics: {
      customer: getCustomerDomain(),
      view: t('tanks:dataTable.analytics')
    },
    canInsert: hasPermission(Resources.Tanks, [UserPermission.EDIT]),
    sortOrder: {
      name:
        hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) || hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO)
          ? 'orderDate'
          : 'tripDate',
      direction:
        hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) || hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO)
          ? SortDirection.DESC
          : SortDirection.ASC
    },
    rowCustomActions: [
      {
        icon: <Pageview fontSize="small" />,
        tooltip: t('tanks:dataTable.customActions.view'),
        visibleIf: 'onlyView',
        onClick: (row: TankModel) => onRowEdit(row, true)
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <Edit fontSize="small" />,
        visibleIf: 'canEdit',
        tooltip: t('tanks:dataTable.customActions.edit'),
        onClick: (row: TankModel) => onRowEdit(row, false)
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <Build fontSize="small" />,
        visibleIf: 'canEditQuantity',
        tooltip: t('tanks:dataTable.customActions.editTravel'),
        onClick: (row: TankModel) => onEditTravelHandler(row)
      },
      {
        userPermission: UserPermission.SEND,
        icon: <Send fontSize="small" />,
        visibleIf: 'canSend',
        tooltip: t('tanks:dataTable.customActions.send'),
        onClick: (row: TankModel) => onSendActionHandler([row])
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <BlockIcon fontSize="small" />,
        visibleIf: 'canCancel',
        tooltip: t('tanks:dataTable.customActions.cancel'),
        onClick: (row: TankModel) => onCancelActionHandler([row])
      },
      {
        icon: <Delete fontSize="small" />,
        visibleIf: 'canDelete',
        tooltip: t('tanks:dataTable.customActions.delete'),
        onClick: onRowDelete
      }
    ],
    selectToolbarCustomActions: [
      {
        userPermission: UserPermission.EDIT,
        icon: <BlockIcon fontSize="small" />,
        tooltip: t('tanks:dataTable.customActions.cancel'),
        onClick: onCancelActionHandler
      },
      {
        userPermission: UserPermission.SEND,
        icon: <Send fontSize="small" />,
        tooltip: t('tanks:dataTable.customActions.send'),
        onClick: onSendActionHandler
      }
    ],
    onRowInsert: async () => {
      history.push(NonAuthRoutes.tanksAdd)
      return true
    },
    onFilterChange: (filter: ColumnFilter[]) => {
      storeDataTableFilterForCurrentPage(filter)
    },
    onSearchChange: (search: string) => {
      storeDataTableSearchForCurrentPage(search)
    },
    onRowsDelete: async () => {}
  }

  //#region Functions

  const getTanksList = async (clientID?: number): Promise<TankModel[] | undefined> => {
    let lastHundred = false
    if (genericFilter) {
      if (genericFilter.value === EnumEstadoViajeOpLogistica.Registrado) {
        const response = await fetchTanksListItemsByRegisteredState()
        setIsFetching(false)
        if (response.success) {
          if (response.data !== undefined && response.data.length > 0) {
            response.data.forEach((item: { carriers: any[]; destinations: any[] }) => {
              item.carriers = [...new Set(item.carriers)]
              item.destinations = [...new Set(item.destinations)]
            })
            return response.data!
          } else {
            showError({
              title: t('tanks:dataTable.error.fetchTankError'),
              errorCode: response.error?.code,
              onRefresh: getTanksList
            })
            return []
          }
        } else {
          return []
        }
      } else if (genericFilter.value === EnumEstadoViajeOpLogistica.Validado) {
        return await fetchTanksListItemsByValidatedState().then(response => {
          setIsFetching(false)
          if (response.success) {
            if (response.data !== undefined && response.data.length > 0) {
              response.data.forEach((item: { carriers: any[]; destinations: any[] }) => {
                item.carriers = [...new Set(item.carriers)]
                item.destinations = [...new Set(item.destinations)]
              })
              return response.data!
            } else {
              showError({
                title: t('tanks:dataTable.error.fetchTankError'),
                errorCode: response.error?.code,
                onRefresh: getTanksList
              })
              return []
            }
          } else {
            return []
          }
        })
      } else if (genericFilter.value === 'lastHundred') {
        lastHundred = true
        return await fetchService({
          startDate: new Date(new Date().setMonth(new Date().getMonth() - 6)),
          endDate: new Date(),
          clientID: clientID ?? (clients.length === 1 ? +clients[0].value : undefined),
          lastHundredTanks: true
        } as any).then(response => {
          clearError()
          setIsFetching(false)
          if (response.success) {
            if (response.data !== undefined && response.data.length > 0) {
              response.data.forEach((item: { carriers: any[]; destinations: any[] }) => {
                item.carriers = [...new Set(item.carriers)]
                item.destinations = [...new Set(item.destinations)]
              })
              return response.data!
            } else {
              showError({
                title: t('tanks:dataTable.error.fetchTankError'),
                errorCode: response.error?.code,
                onRefresh: getTanksList
              })
              return []
            }
          } else {
            return []
          }
        })
      }
    }

    let dateStart = dateRange.start
    let dateEnd = dateRange.end

    if (!dateStart || !dateEnd) {
      return
    }

    return await fetchService({
      startDate: dateStart,
      endDate: dateEnd,
      clientID: clientID ?? (clients.length === 1 ? +clients[0].value : undefined),
      lastHundredTanks: lastHundred // isForUserFetch(fetchService)
    }).then(response => {
      clearError()
      setIsFetching(false)
      if (response.success) {
        if (response.data !== undefined && response.data.length > 0) {
          response.data.forEach((item: { carriers: any[]; destinations: any[] }) => {
            item.carriers = [...new Set(item.carriers)]
            item.destinations = [...new Set(item.destinations)]
          })
          return response.data!
        } else {
          showError({
            title: t('tanks:dataTable.error.fetchTankError'),
            errorCode: response.error?.code,
            onRefresh: getTanksList
          })
          return []
        }
      } else {
        return []
      }
    })
  }

  const monitorTanks = async (pendingTanks: TankModel[]) => {
    let currentTanks = data

    return await checkTanksState(
      pendingTanks.map(op => op.id!),
      [
        EnumEstadoViajeOpLogistica.Programado,
        EnumEstadoViajeOpLogistica.Anulado,
        EnumEstadoViajeOpLogistica.Rechazado
      ]
    ).then(changedTanks => {
      changedTanks.forEach((changedTank: TankModel) => {
        if (currentTanks && currentTanks !== undefined) {
          const index = currentTanks.findIndex(x => x.id === changedTank.id)

          if (index !== -1) {
            currentTanks = [
              ...currentTanks!.slice(0, index),
              completeDataTableActions({ ...changedTank }),
              ...currentTanks!.slice(index + 1)
            ]
            setData(currentTanks)
          }
        }

        const pendingIndex = pendingTanks.findIndex(x => x.id === changedTank.id)

        pendingTanks = [
          ...pendingTanks!.slice(0, pendingIndex),
          ...pendingTanks!.slice(pendingIndex + 1)
        ]
      })

      return pendingTanks
    })
  }

  const completeDataTableActions = (item: TankModel): TankModel => {
    return {
      ...item,
      actions:
        item.actionsConfiguration &&
        item.actionsConfiguration.length > 0 &&
        generateIconsFromConfiguration(
          item,
          filterApplicableActionsConfiguration(item, item.actionsConfiguration),
          new Map<ActionIcons, Function>([])
        )
    }
  }

  //#endregion

  //#region customFilter

  const onPrevalidateCustomFilter = async (filterToApply: any): Promise<boolean> => {
    const totalCount = await countReports(filterToApply.startDate, filterToApply.endDate)
    if (totalCount !== undefined && totalCount < 1000) {
      return true
    }
    return false
  }

  const countReports = async (startDate: Date, endDate: Date): Promise<number | undefined> => {
    const reports = await getReports(startDate, endDate)
    return reports ? reports.length : undefined
  }

  const getReports = async (startDate: Date, endDate: Date): Promise<TankModel[] | undefined> => {
    return await fetchService({
      startDate,
      endDate,
      clientID: clients?.length === 1 ? clients[0].value : undefined,
      //clientID: undefined,
      lastHundredTanks: undefined
    }).then(r => {
      if (r.success) {
        return r.data
      } else {
        return undefined
      }
    })
  }

  const columns = useMemo(
    () => getTanksListColumns({ carriers, destinations, originPlants }),
    [carriers, destinations, originPlants]
  )

  useEffect(() => {
    if (!genericFilter) return
  }, [genericFilter])
  //#endregion

  return (
    <Page notification={globalNotification}>
      <PageHeader title={t('tanks:title')}>
        <GenericFilters
          filters={filters.genericFilters}
          activeFilter={genericFilter}
          onSelectFilter={onSelectGenericFilter}
        />
        <DateFilter
          inactive={customFilter || genericFilter?.enabled}
          enabled={!isFetching}
          initialFilter={dateFilter?.activeDateFilter ?? DEFAULT_ACTIVE_FILTER}
          onSelectDate={onSelectDateHandler}
          filters={filters.dateFilters}
        ></DateFilter>
        {filters.customFilter && (
          <CustomFilter
            onApplyFilter={onApplyCustomFilter}
            onPrevalidateFilter={onPrevalidateCustomFilter}
            appliedFilter={customFilter}
            enabled={!isFetching}
          ></CustomFilter>
        )}
      </PageHeader>
      <DataTable
        data={data}
        columns={columns}
        options={dataTableOptions}
        info={DataTableErrorHandler(errorProps)}
        userPermissions={getUserPermissions(Pages.Tanks)}
      />
      <CustomFormDialog maxWidth={'xs'} {...formProps} />
    </Page>
  )
}
